import type { ObjectiveOptionGroups } from '@/state/experiments/objective-options';
import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { isUnmapped } from '@/state/experiments/objective-options';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandInput, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { EventTag } from './EventTag';
import { ObjectiveCommandGroups } from './ObjectiveCommandGroups';

type SecondaryObjectivesPickerProps = {
  /** The selected secondaries, in the order the report shows them. */
  selected: readonly string[];
  /** The experiment's primary: listed but not pickable, since it would report twice. */
  primary: string;
  groups: ObjectiveOptionGroups;
  /** Saves the new list; resolves false when the save failed. */
  onChange: (next: string[]) => Promise<boolean>;
  /** Called with the event just added, once saved, so the card can offer to map it. */
  onAdded: (name: string) => void;
  /** List only, no remove or add: the experiment is running and its objectives are locked. */
  readOnly?: boolean;
};

const ROW_CLASS = 'border-line flex h-10 items-center gap-3 border-b pr-2 pl-3.5 last:border-b-0';

/**
 * The secondary objectives as a numbered list in report order, each removable, ending in an "Add
 * secondary objective" row that opens a searchable list of the website's events: mapped as
 * secondary first, then as primary, then not mapped. A row whose event is not mapped says so
 * rather than disappearing.
 */
export const SecondaryObjectivesPicker = ({
  selected,
  primary,
  groups,
  onChange,
  onAdded,
  readOnly = false,
}: SecondaryObjectivesPickerProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const add = async (name: string) => {
    setIsOpen(false);
    if (await onChange([...selected, name])) onAdded(name);
  };
  const remove = (name: string) => void onChange(selected.filter((goal) => goal !== name));

  const offer = (names: readonly string[]) => names.filter((name) => !selected.includes(name));

  return (
    <div className="border-line w-full max-w-[560px] overflow-hidden rounded-lg border">
      <ol className="m-0 list-none p-0">
        {selected.map((name, index) => (
          <li key={name} className={ROW_CLASS}>
            <span className="text-dim w-4 shrink-0 font-mono text-micro tabular-nums">{index + 1}</span>
            <span className="text-foreground min-w-0 flex-1 truncate font-mono text-meta">{name}</span>
            {isUnmapped(groups, name) ? <EventTag tone="muted">not mapped</EventTag> : null}
            {readOnly ? null : (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => remove(name)}
                className="text-dim hover:text-danger size-7 shrink-0 rounded-md"
                aria-label={`Remove ${name}`}
              >
                <X className="size-4" strokeWidth={1.5} />
              </Button>
            )}
          </li>
        ))}
      </ol>

      {selected.length === 0 ? (
        <p className="supporting m-0 flex h-10 items-center px-3.5">No secondary objectives.</p>
      ) : null}

      {readOnly ? null : (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="bg-panel2 text-brand hover:bg-hover focus-visible:ring-ring border-line flex h-10 w-full items-center gap-2 border-t px-3.5 text-left text-meta font-medium transition-colors focus-visible:ring-1 focus-visible:outline-none"
            >
              <Plus className="size-3.5" strokeWidth={2} />
              Add secondary objective
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-[var(--radix-popover-trigger-width)] max-w-[calc(100vw-2rem)] min-w-[280px] p-0"
          >
            <Command>
              <CommandInput placeholder="Search events" className="text-body" />
              <CommandList>
                <CommandEmpty className="supporting px-3 py-4">No matching events.</CommandEmpty>
                <ObjectiveCommandGroups
                  onPick={(name) => void add(name)}
                  disabled={{ name: primary, reason: 'primary' }}
                  sections={[
                    { heading: 'Mapped as secondary', names: offer(groups.secondary) },
                    { heading: 'Mapped as primary', names: offer(groups.primary) },
                    { heading: 'Not mapped', names: offer(groups.notMapped) },
                  ]}
                />
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};
